"use client";

import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, EyeOff, Link2, Settings2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ConnectionConfig } from "../../connection-studio";

interface DatabaseConfigFormProps {
  formData: ConnectionConfig;
  onChange: (updates: Partial<ConnectionConfig>) => void;
}

const DB_TYPES = [
  { value: "postgresql", label: "PostgreSQL", port: 5432 },
  { value: "mysql", label: "MySQL", port: 3306 },
  { value: "mongodb", label: "MongoDB", port: 27017 },
  { value: "mssql", label: "MSSQL", port: 1433 },
];

export function DatabaseConfigForm({ formData, onChange }: DatabaseConfigFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [inputMode, setInputMode] = useState<"fields" | "connection_string">(
    () => (formData.config?.connection_string ? "connection_string" : "fields"),
  );

  const config = formData.config || {};
  const authConfig = formData.auth_config || {};

  const updateConfig = (key: string, value: unknown) => {
    onChange({ config: { ...config, [key]: value } });
  };

  const updateAuth = (key: string, value: unknown) => {
    onChange({ auth_config: { ...authConfig, [key]: value } });
  };

  const selectedDbType = (config.db_type as string) || "postgresql";
  const defaultPort = DB_TYPES.find((t) => t.value === selectedDbType)?.port ?? 5432;

  return (
    <div className="space-y-5">
      {/* Database Type */}
      <div className="space-y-3">
        <Label className="text-white/80">Database Type</Label>
        <div className="flex flex-wrap gap-2">
          {DB_TYPES.map((db) => (
            <Badge
              key={db.value}
              variant={selectedDbType === db.value ? "default" : "outline"}
              className={cn(
                "cursor-pointer px-3 py-2 text-sm",
                selectedDbType === db.value
                  ? "bg-sky-500/20 text-sky-400 hover:bg-sky-500/30"
                  : "border-white/10 bg-white/5 text-white/60 hover:bg-white/10 hover:text-white",
              )}
              onClick={() => {
                updateConfig("db_type", db.value);
                if (!config.port || config.port === defaultPort) {
                  updateConfig("port", db.port);
                }
              }}
            >
              {db.label}
            </Badge>
          ))}
        </div>
      </div>

      {/* Input Mode Toggle */}
      <div className="flex gap-1 rounded-md border border-white/10 bg-white/5 p-0.5 w-fit">
        <button
          type="button"
          onClick={() => setInputMode("fields")}
          className={cn(
            "flex items-center gap-1.5 rounded px-2.5 py-1 text-xs transition-colors",
            inputMode === "fields"
              ? "bg-white/10 text-white"
              : "text-white/50 hover:text-white/70",
          )}
        >
          <Settings2 className="h-3 w-3" />
          Individual Fields
        </button>
        <button
          type="button"
          onClick={() => setInputMode("connection_string")}
          className={cn(
            "flex items-center gap-1.5 rounded px-2.5 py-1 text-xs transition-colors",
            inputMode === "connection_string"
              ? "bg-white/10 text-white"
              : "text-white/50 hover:text-white/70",
          )}
        >
          <Link2 className="h-3 w-3" />
          Connection String
        </button>
      </div>

      {inputMode === "connection_string" ? (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-4">
          <p className="text-xs font-medium uppercase tracking-wider text-white/50">
            Connection String
          </p>
          <div className="space-y-2">
            <Label className="text-white/80">
              Connection URI <span className="text-rose-400">*</span>
            </Label>
            <Input
              value={(config.connection_string as string) || ""}
              onChange={(e) => updateConfig("connection_string", e.target.value)}
              placeholder={
                selectedDbType === "postgresql"
                  ? "postgresql://user:pass@host:5432/dbname"
                  : selectedDbType === "mysql"
                    ? "mysql://user:pass@host:3306/dbname"
                    : selectedDbType === "mongodb"
                      ? "mongodb://user:pass@host:27017/dbname"
                      : "Server=host;Database=dbname;User Id=user;Password=pass;"
              }
              className="border-white/10 bg-white/5 font-mono text-sm text-white placeholder:text-white/40"
            />
            <p className="text-xs text-white/40">
              Full connection string including credentials
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Server Connection */}
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-4">
            <p className="text-xs font-medium uppercase tracking-wider text-white/50">
              Server Connection
            </p>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-2">
                <Label className="text-white/80">
                  Host <span className="text-rose-400">*</span>
                </Label>
                <Input
                  value={(config.host as string) || ""}
                  onChange={(e) => updateConfig("host", e.target.value)}
                  placeholder="localhost or db.example.com"
                  className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white/80">Port</Label>
                <Input
                  type="number"
                  value={(config.port as number) || defaultPort}
                  onChange={(e) => updateConfig("port", parseInt(e.target.value) || defaultPort)}
                  className="border-white/10 bg-white/5 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white/80">
                Database Name <span className="text-rose-400">*</span>
              </Label>
              <Input
                value={(config.database as string) || ""}
                onChange={(e) => updateConfig("database", e.target.value)}
                placeholder="my_database"
                className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
              />
            </div>
          </div>

          {/* Authentication */}
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-4">
            <p className="text-xs font-medium uppercase tracking-wider text-white/50">
              Authentication
            </p>

            <div className="space-y-2">
              <Label className="text-white/80">
                Username <span className="text-rose-400">*</span>
              </Label>
              <Input
                value={(authConfig.username as string) || ""}
                onChange={(e) => updateAuth("username", e.target.value)}
                placeholder="Enter username"
                className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white/80">
                Password <span className="text-rose-400">*</span>
              </Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={(authConfig.password as string) || ""}
                  onChange={(e) => updateAuth("password", e.target.value)}
                  placeholder="Enter password"
                  className="border-white/10 bg-white/5 pr-10 text-white placeholder:text-white/40"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-white/40 hover:text-white"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* SSL Toggle */}
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-3">
        <p className="text-xs font-medium uppercase tracking-wider text-white/50">
          Security
        </p>
        <label className="flex items-center justify-between">
          <div>
            <span className="text-sm text-white/80">Require SSL</span>
            <p className="text-xs text-white/40">Encrypt the database connection</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={!!config.ssl}
            onClick={() => updateConfig("ssl", !config.ssl)}
            className={cn(
              "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors",
              config.ssl ? "bg-sky-500" : "bg-white/20",
            )}
          >
            <span
              className={cn(
                "pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transition-transform mt-0.5",
                config.ssl ? "translate-x-4 ml-0.5" : "translate-x-0.5",
              )}
            />
          </button>
        </label>
      </div>
    </div>
  );
}
